import React, { useEffect, useRef, useState } from 'react'
import { TranslationProvider } from '~/components/shared/TranslationProvider'
import BenefitsProvider, { useBenefits } from '../BenefitsProvider'
import ScheduleDialog from '../ProfessionalServices/ScheduleDialog'
import { useTranslation } from '~/hooks/useTranslation'
import translations from '../locales.json'
import { Button } from '~/lib/ui/button'
import * as AtomButton from '~/components/_atoms/Button/index'
import {
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline'
import { cn } from '~/lib/utils'
import { Step } from './Step'
import RightRail, { RightRailModal } from './RightRail'
import { useIsMobile, useIsTablet } from '~/hooks/useMediaQuery'
import YnsIcon from '~/components/icons/YnsIcon'
import { BackToServices } from '../ProfessionalServices/ServiceDetail'
import UpdateCandidateProfile from '~/components/UpdateCandidateProfile'

const ResumeReview = ({ ...props }) => {
  const { t } = useTranslation()
  const { current_user } = props
  const { isPremium } = current_user
  const programLevel = isPremium ? 'Premium' : 'Flex'
  return (
    <div className="home-container min-h-0 overflow-y-auto">
      <div className="flex min-h-44 flex-col bg-willow-brook p-4 md:p-9">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="!font-roboto-slab text-2xl text-[32px] font-medium !text-intoo-green-coachcta">
            <span className="!font-normal"></span>{' '}
            {t(`${programLevel}.title_dark`)}
          </h1>
        </div>
        <p>{t(`${programLevel}.intro_paragraph`)}</p>
        {isPremium && (
          <div className="!mb-4">
            <p>
              <BackToServices />
            </p>
          </div>
        )}
      </div>
      <ResumeReviewInner {...props} />
    </div>
  )
}

const ResumeReviewInner = ({ ...props }) => {
  const { t } = useTranslation()
  const { current_user } = props
  const { isPremium, profileIncomplete, isLiaison } = current_user
  const {
    resumeReview,
    onScheduleClick,
    coach,
    programLevel,
    setShowScheduleDialog,
    select,
  } = useBenefits()
  const isTablet = useIsTablet()

  const [isCollapsed, setIsCollapsed] = useState(resumeReview !== null) // Manage collapsed state

  const scheduleScrollRef = useRef(null)
  const resumeReviewScrollRef = useRef(null)
  const reviewCompleteScrollRef = useRef(null)

  const params = window.location.search

  const meetingViaCalendly = resumeReview?.meeting_via_calendly

  useEffect(() => {
    if (params.includes('utm_campaign=resume-rev-coach-assigned')) {
      scheduleScrollRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    } else if (params.includes('utm_campaign=resume-rev')) {
      resumeReviewScrollRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    } else if (params.includes('utm_campaign=res-com')) {
      reviewCompleteScrollRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [params])

  useEffect(() => {
    if (resumeReview) {
      setIsCollapsed(true)
    }
  }, [resumeReview])

  const steps = [
    'chooseReviewType',
    'submitResume',
    'scheduleMeeting',
    'receiveFeedback',
    'askForRevisions',
    'finalFeedback',
  ].filter((step) => !(step === 'scheduleMeeting' && !meetingViaCalendly))

  const [resumeDocsModalOpen, setResumeDocsModalOpen] = useState(false)
  const [showUpdateProfileDialog, setShowUpdateProfileDialog] = useState(false)

  const toggleShowUpdateProfileDialog = () => {
    setShowUpdateProfileDialog(!showUpdateProfileDialog)
    setShowScheduleDialog(false)
  }

  return (
    <>
      <div
        className={cn(
          'mx-3 -mt-6 mb-4 flex h-full gap-6 rounded-3xl border border-cod-gray-200 bg-white p-4 font-roboto text-cod-gray-800 transition-all duration-300 md:mx-8 md:-mt-10 ',
          isCollapsed ? 'md:h-28 md:px-9 md:py-2' : 'flex-col md:p-9 ',
        )}
      >
        <ScheduleDialog
          current_user={current_user}
          coach={coach}
          flexResumeReview={true}
          toggleShowUpdateProfileDialog={toggleShowUpdateProfileDialog}
        />
        <UpdateCandidateProfile
          selectProgram={select}
          showUpdateProfileDialog={showUpdateProfileDialog}
          toggleShowUpdateProfileDialog={toggleShowUpdateProfileDialog}
        />

        <div className="flex flex-grow flex-col items-center md:flex-row md:gap-20">
          <div className="flex flex-grow flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-intoo-green-coachcta p-3">
                <DocumentTextIcon className="h-5 w-5 text-white" />
              </div>
              <h2 className="subtitle text-intoo-green-coachcta">
                {t(`${programLevel}.overview.title`)}
              </h2>
            </div>
            {!isCollapsed && (
              <>
                <p className="!mb-0">{t(`${programLevel}.overview.intro1`)}</p>
                <p className="!mb-0">{t(`${programLevel}.overview.intro2`)}</p>
              </>
            )}
          </div>
          <img
            alt="people network"
            src="https://www-yns-com.s3.amazonaws.com/interview/prepare_to_interview/flex_coach_image.png"
            className={isCollapsed ? 'h-20' : 'h-full'}
          />
          {resumeReview && (
            <button onClick={() => setIsCollapsed(!isCollapsed)}>
              {isCollapsed ? (
                <ChevronDownIcon className="h-10 w-10" />
              ) : (
                <ChevronUpIcon className="h-10 w-10" />
              )}
            </button>
          )}
        </div>

        {!isCollapsed && (
          <div className="grid gap-12 md:grid-cols-2">
            <div className="flex flex-col justify-between rounded-3xl border border-[#2F2F2F] bg-willow-brook p-6">
              <div>
                <h3 className="subtitle mb-6 text-intoo-green-coachcta">
                  {coach?.isLiaison
                    ? t(`${programLevel}.meetingWithCoach.title_liaison`)
                    : t(`${programLevel}.meetingWithCoach.title`, {
                        coach_name: coach?.first_name,
                      })}
                </h3>
                <p className="!mb-6">
                  {t(`${programLevel}.meetingWithCoach.p1`)}
                </p>
                {!isPremium && (
                  <p className="!mb-6">{t(`Flex.meetingWithCoach.p2`)}</p>
                )}
              </div>
              <Button
                variant="coachcta"
                onClick={() =>
                  onScheduleClick(
                    'ServiceRequest::ResumeReview',
                    t(`${programLevel}.meetingWithCoach.button`),
                    true,
                  )
                }
                className="w-full"
                disabled={resumeReview}
              >
                {profileIncomplete && isLiaison
                  ? t(`${programLevel}.updateYourProfile`)
                  : t(`${programLevel}.meetingWithCoach.button`)}
              </Button>
            </div>

            <div className="flex h-full flex-col justify-between rounded-3xl border border-[#2F2F2F] bg-willow-brook p-6">
              <div>
                <h3 className="subtitle mb-6 text-intoo-green-coachcta">
                  {t(`${programLevel}.platformReview.title`)}
                </h3>
                <p className="!mb-6">
                  {t(`${programLevel}.platformReview.p1`)}
                </p>
              </div>
              <Button
                variant="coachcta"
                onClick={() =>
                  onScheduleClick(
                    'ServiceRequest::ResumeReview',
                    t(`${programLevel}.platformReview.button`),
                    false,
                  )
                }
                className="w-full"
                disabled={resumeReview}
              >
                {profileIncomplete && isLiaison
                  ? t(`${programLevel}.updateYourProfile`)
                  : t(`${programLevel}.platformReview.button`)}
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-center">
        {isTablet && (
          <AtomButton.default
            onClick={() => setResumeDocsModalOpen(true)}
            color="secondary"
            variant="outline"
          >
            <div className="subtitle flex items-center">
              {t('RightRail.title')}
              <YnsIcon name="document" className="ml-2 h-6 w-6" />
            </div>
          </AtomButton.default>
        )}
      </div>
      <RightRailModal
        resumeDocsModalOpen={resumeDocsModalOpen}
        setResumeDocsModalOpen={setResumeDocsModalOpen}
      />

      <RightRailWrapper>
        <div className="mx-3 mb-4 pb-4 pt-4 font-roboto text-cod-gray-800 md:mx-8 md:mb-9 md:w-3/4">
          <div>
            {steps.map((stepKey, idx) => (
              <Step
                key={`step-${stepKey}-${idx}`}
                stepKey={stepKey}
                idx={idx}
                steps={steps}
                scheduleScrollRef={scheduleScrollRef}
                resumeReviewScrollRef={resumeReviewScrollRef}
                reviewCompleteScrollRef={reviewCompleteScrollRef}
              />
            ))}
          </div>
        </div>
        {!isTablet && <RightRail />}
      </RightRailWrapper>
    </>
  )
}

const RightRailWrapper = ({ children }) => {
  const isTablet = useIsMobile()
  if (isTablet) return children
  return <div className="flex">{children}</div>
}

const Index = ({ ...props }) => {
  const { locale } = props
  return (
    <TranslationProvider
      translations={translations}
      locale={locale}
      scope="javascript.components.coach_services.ResumeReview"
    >
      <BenefitsProvider {...props}>
        <ResumeReview {...props} />
      </BenefitsProvider>
    </TranslationProvider>
  )
}

export default Index
